import { toLocalDateStr } from "../lib/date";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { getAdvisorySummary, getAdvisoryGroups, assignStudentsToAdvisories, getAppSettings, updateAppSettings } from "../lib/api";
import { useAuth } from "../App";
import SignUpsTab from "../components/signups/SignUpsTab";
import TripsTab from "../components/signups/TripsTab";

// Helper to get Monday of a given week
const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

// Monday of the PREVIOUS week (default view so students see the completed week)
const getLastWeekMonday = () => {
  const m = getMonday(new Date());
  m.setDate(m.getDate() - 7);
  return m;
};

// Helper to format date for display
const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Get week options (current week and past 8 weeks)
const getWeekOptions = () => {
  const weeks = [];
  let current = getMonday(new Date());
  
  for (let i = 0; i < 9; i++) {
    const monday = new Date(current);
    const friday = new Date(current);
    friday.setDate(friday.getDate() + 4);
    
    weeks.push({
      value: toLocalDateStr(monday),
      label: i === 0 
        ? `This Week (${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
        : i === 1
          ? `Last Week (${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
          : `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    });
    
    current.setDate(current.getDate() - 7);
  }
  
  return weeks;
};

// Score color helper
const getScoreColor = (score) => {
  if (score === null || score === undefined) return "text-gray-400";
  if (score >= 3.5) return "text-green-600 bg-green-50";
  if (score >= 2.5) return "text-blue-600 bg-blue-50";
  if (score >= 1.5) return "text-yellow-600 bg-yellow-50";
  return "text-red-600 bg-red-50";
};

// Attendance status colors
const getAttendanceColor = (status) => {
  switch (status) {
    case "present": return "bg-green-500";
    case "tardy": return "bg-yellow-500";
    case "absent_excused":
    case "excused": return "bg-orange-400";
    case "absent_unexcused":
    case "unexcused":
    case "absent": return "bg-red-500";
    case "left_early_unexcused": return "bg-purple-500";
    default: return "bg-gray-300";
  }
};

// Attendance status to short label
const getAttendanceLabel = (status) => {
  switch (status) {
    case "present": return "P";
    case "tardy": return "T";
    case "absent_excused":
    case "excused": return "AE";
    case "absent_unexcused":
    case "unexcused": return "AU";
    case "absent": return "A";
    case "left_early_unexcused": return "LE";
    default: return "-";
  }
};

export default function AdvisoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const advisoryTab = ["summary", "signups", "trips"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "summary";
  const setAdvisoryTab = (v) => {
    if (v === "summary") setSearchParams({}, { replace: true });
    else setSearchParams({ tab: v }, { replace: true });
  };
  const { user, isAdmin } = useAuth();
  const [advisoryGroups, setAdvisoryGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("1");
  const [selectedWeek, setSelectedWeek] = useState(toLocalDateStr(getLastWeekMonday()));
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  // Slideshow settings
  const [slideshowUrl, setSlideshowUrl] = useState("");
  const [slideshowTitle, setSlideshowTitle] = useState("Weekly Advisory Slideshow");
  const [editSlideshowOpen, setEditSlideshowOpen] = useState(false);
  const [tempUrl, setTempUrl] = useState("");
  const [tempTitle, setTempTitle] = useState("");

  const weekOptions = getWeekOptions();

  const fetchGroups = async () => {
    try {
      const res = await getAdvisoryGroups();
      setAdvisoryGroups([...(res.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      
      // If user is assigned to an advisory, default to that
      if (user?.advisory_group) {
        setSelectedGroup(user.advisory_group);
      }
    } catch (err) {
      toast.error("Failed to load advisory groups");
    }
  };
  
  const fetchAppSettings = async () => {
    try {
      const res = await getAppSettings();
      setSlideshowUrl(res.data.slideshow_url || "");
      setSlideshowTitle(res.data.slideshow_title || "Weekly Advisory Slideshow");
    } catch (err) {
      console.error("Failed to load app settings");
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await getAdvisorySummary(selectedGroup, selectedWeek);
      setSummaryData(res.data);
    } catch (err) {
      toast.error("Failed to load advisory summary");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStudents = async () => {
    setAssigning(true);
    try {
      const res = await assignStudentsToAdvisories();
      toast.success(res.data.message);
      fetchGroups();
      fetchSummary();
    } catch (err) {
      toast.error("Failed to assign students");
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchAppSettings();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [selectedGroup, selectedWeek]);
  
  const handleLaunchSlideshow = () => {
    if (slideshowUrl) {
      window.open(slideshowUrl, '_blank');
    } else {
      toast.error("Slideshow URL not configured");
    }
  };
  
  const handleOpenEditSlideshow = () => {
    setTempUrl(slideshowUrl);
    setTempTitle(slideshowTitle);
    setEditSlideshowOpen(true);
  };
  
  const handleSaveSlideshow = async () => {
    try {
      await updateAppSettings({ slideshow_url: tempUrl, slideshow_title: tempTitle });
      setSlideshowUrl(tempUrl);
      setSlideshowTitle(tempTitle);
      setEditSlideshowOpen(false);
      toast.success("Slideshow settings updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    }
  };

  const totalUnassigned = advisoryGroups.reduce((sum, g) => sum + (g.student_count === 0 ? 0 : 0), 0);

  return (
    <div className="space-y-6" data-testid="advisory-page">
      {/* Slideshow Banner */}
      <Card className="bg-gradient-to-r from-[#00305E] to-[#00305E]/80 text-white">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2"/><path d="m9 8 6 4-6 4Z"/>
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg">{slideshowTitle}</h3>
                <p className="text-white/70 text-sm">Click to open this week's presentation</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleLaunchSlideshow}
                className="bg-white text-[#00305E] hover:bg-white/90"
                data-testid="launch-slideshow-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                Launch Slideshow
              </Button>
              {isAdmin() && (
                <Dialog open={editSlideshowOpen} onOpenChange={setEditSlideshowOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-white hover:bg-white/20"
                      onClick={handleOpenEditSlideshow}
                      data-testid="edit-slideshow-btn"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                      </svg>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Slideshow Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input 
                          value={tempTitle}
                          onChange={(e) => setTempTitle(e.target.value)}
                          placeholder="Weekly Advisory Slideshow"
                          data-testid="slideshow-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>URL</Label>
                        <Input 
                          value={tempUrl}
                          onChange={(e) => setTempUrl(e.target.value)}
                          placeholder="https://drive.google.com/..."
                          data-testid="slideshow-url-input"
                        />
                        <p className="text-sm text-muted-foreground">
                          Paste the Google Drive folder or presentation link
                        </p>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditSlideshowOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveSlideshow} data-testid="save-slideshow-btn">
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={advisoryTab} onValueChange={setAdvisoryTab} className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="summary" data-testid="advisory-tab-summary">Weekly Summary</TabsTrigger>
          <TabsTrigger value="signups" data-testid="advisory-tab-signups">Sign-Ups</TabsTrigger>
          <TabsTrigger value="trips" data-testid="advisory-tab-trips">Trips &amp; Events</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Advisory</h1>
          <p className="text-muted-foreground mt-1">Weekly student summaries for advisory meetings</p>
        </div>
        <Button 
          onClick={handleAssignStudents}
          disabled={assigning}
          variant="outline"
          className="border-[#00305E] text-[#00305E]"
          data-testid="assign-students-btn"
        >
          {assigning ? "Assigning..." : "Auto-Assign Unassigned Students"}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Advisory Group</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger data-testid="advisory-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {advisoryGroups.map((groupData) => (
                    <SelectItem key={groupData.group} value={groupData.group}>
                      {groupData.name || `Advisory ${groupData.group}`} ({groupData.student_count || 0} students)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Week</Label>
              <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                <SelectTrigger data-testid="advisory-week-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {weekOptions.map((week) => (
                    <SelectItem key={week.value} value={week.value}>
                      {week.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Advisors</Label>
              <div className="h-10 flex items-center text-sm text-muted-foreground">
                {summaryData?.advisors?.length > 0 
                  ? summaryData.advisors.map(a => a.name).join(", ")
                  : "No advisors assigned"}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summaryData && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-[#00305E]">{summaryData.summary.total_students}</p>
              <p className="text-sm text-muted-foreground">Students</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">{summaryData.summary.total_present}</p>
              <p className="text-sm text-muted-foreground">Present (P)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-red-600">{summaryData.summary.total_absent_unexcused || 0}</p>
              <p className="text-sm text-muted-foreground">Absent Unexcused (AU)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-orange-500">{summaryData.summary.total_absent_excused || 0}</p>
              <p className="text-sm text-muted-foreground">Absent Excused (AE)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-yellow-600">{summaryData.summary.total_tardy}</p>
              <p className="text-sm text-muted-foreground">Tardy (T)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-purple-600">{summaryData.summary.total_left_early || 0}</p>
              <p className="text-sm text-muted-foreground">Left Early (LE)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Student Summaries */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00305E]"></div>
        </div>
      ) : summaryData?.students?.length > 0 ? (
        <div className="space-y-4">
          {summaryData.students.map((student) => (
            <Card 
              key={student.student_id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/students/${student.student_id}`)}
              data-testid={`advisory-student-${student.student_id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-6">
                  {/* Student Info */}
                  <div className="flex-shrink-0 w-48">
                    <h3 className="font-semibold text-lg text-[#00305E]">{student.student_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Attendance: {student.attendance.rate}%
                    </p>
                  </div>

                  {/* Attendance by Day */}
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Attendance</p>
                    <div className="flex gap-2">
                      {summaryData.week_dates?.map((date) => {
                        const status = student.attendance.by_date[date] || "not_recorded";
                        return (
                          <div key={date} className="text-center">
                            <p className="text-xs text-muted-foreground mb-1">
                              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                            </p>
                            <div 
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAttendanceColor(status)}`}
                              title={`${formatDate(date)}: ${status}`}
                            >
                              {getAttendanceLabel(status)}
                            </div>
                          </div>
                        );
                      })}
                      <div className="text-center ml-2 pl-2 border-l">
                        <p className="text-xs text-muted-foreground mb-1">Total</p>
                        <div className="flex gap-1 text-xs flex-wrap">
                          <span className="text-green-600 font-medium">{student.attendance.present}P</span>
                          <span className="text-red-600 font-medium">{student.attendance.absent_unexcused || 0}AU</span>
                          <span className="text-orange-500 font-medium">{student.attendance.absent_excused || 0}AE</span>
                          <span className="text-yellow-600 font-medium">{student.attendance.tardy}T</span>
                          <span className="text-purple-600 font-medium">{student.attendance.left_early || 0}LE</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assessment Averages */}
                  <div className="flex-shrink-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Assessments ({student.assessments.total_assessments} total{student.assessments.did_not_attend > 0 ? `, ${student.assessments.did_not_attend} DNA` : ''})
                    </p>
                    <div className="flex gap-1">
                      {[
                        { key: "attendance", label: "Att" },
                        { key: "engagement", label: "Eng" },
                        { key: "communication", label: "Com" },
                        { key: "behavior", label: "Beh" },
                        { key: "attention", label: "Atn" },
                        { key: "task_completion", label: "Task" },
                      ].map(({ key, label }) => (
                        <div key={key} className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">{label}</p>
                          <div className={`w-9 h-9 rounded flex items-center justify-center text-xs font-semibold ${getScoreColor(student.assessments.averages[key])}`}>
                            {student.assessments.averages[key] !== null 
                              ? student.assessments.averages[key].toFixed(1)
                              : "-"}
                          </div>
                        </div>
                      ))}
                      <div className="text-center ml-1 pl-1 border-l">
                        <p className="text-xs text-muted-foreground mb-1">Avg</p>
                        <div className={`w-9 h-9 rounded flex items-center justify-center text-xs font-bold ${getScoreColor(student.assessments.averages.overall)}`}>
                          {student.assessments.averages.overall !== null 
                            ? student.assessments.averages.overall.toFixed(1)
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <p className="text-muted-foreground">No students in {(advisoryGroups.find(g => g.group === selectedGroup)?.name) || `Advisory ${selectedGroup}`}</p>
            <p className="text-sm text-gray-400 mt-1">Use the "Auto-Assign Unassigned Students" button to assign students</p>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        <TabsContent value="signups">
          <SignUpsTab />
        </TabsContent>

        <TabsContent value="trips">
          <TripsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
